import { getActiveMapDefinition } from '../map/map-registry';
import { isMobile } from '../core/device';
import { getCallouts } from './map-callouts';

const CANVAS_SIZE = 200;
const MAP_PX = 180;
const CENTER = CANVAS_SIZE / 2;

// Callout label styling
const CALLOUT_FONT = '11px monospace';
const CALLOUT_TEXT_COLOR = 'rgba(220,220,220,0.85)';
const CALLOUT_BG_COLOR = 'rgba(0,0,0,0.45)';
const CALLOUT_PAD_X = 2;
const CALLOUT_PAD_Y = 1;

// Bomb icon styling
const BOMB_ICON_RADIUS = 4;
const BOMB_FLASH_RATE = 3; // flashes per second

type StructureRect = { x: number; z: number; w: number; h: number };

const MAP_LAYOUTS: Record<string, { arenaHalf: number; structures: StructureRect[] }> = {
  de_outpost_v6: {
    arenaHalf: 80,
    structures: [
      { x: 0, z: 72, w: 58, h: 2 },
      { x: -29, z: 63, w: 2, h: 18 },
      { x: 29, z: 63, w: 2, h: 18 },
      { x: -12, z: 56, w: 2, h: 12 },
      { x: 12, z: 56, w: 2, h: 12 },
      { x: -22, z: 67, w: 10, h: 8 },
      { x: 20, z: 65, w: 5, h: 8 },
      { x: -58, z: 16, w: 2, h: 68 },
      { x: -40, z: 34, w: 2, h: 30 },
      { x: -40, z: -12, w: 2, h: 23 },
      { x: -49, z: 30, w: 14, h: 8 },
      { x: -50, z: 44, w: 7, h: 4 },
      { x: -49, z: 0, w: 18, h: 14 },
      { x: -10, z: 3, w: 2, h: 38 },
      { x: 18, z: 12, w: 2, h: 18 },
      { x: 18, z: -10, w: 2, h: 12 },
      { x: 0, z: 0, w: 9, h: 9 },
      { x: 24, z: 0, w: 12, h: 10 },
      { x: -20, z: -25, w: 13, h: 17 },
      { x: -40, z: -45, w: 30, h: 2 },
      { x: -55, z: -32, w: 2, h: 26 },
      { x: -43, z: -35, w: 14, h: 14 },
      { x: 22, z: 28, w: 2, h: 20 },
      { x: 22, z: 3, w: 2, h: 22 },
      { x: 50, z: 30, w: 2, h: 16 },
      { x: 50, z: 1, w: 2, h: 26 },
      { x: 36, z: 13, w: 30, h: 18 },
      { x: 40, z: -42, w: 30, h: 2 },
      { x: 25, z: -27, w: 2, h: 30 },
      { x: 55, z: -27, w: 2, h: 30 },
      { x: 40, z: -32, w: 18, h: 10 },
      { x: 0, z: -72, w: 50, h: 2 },
      { x: 0, z: -65, w: 7, h: 7 },
      { x: -13, z: -67, w: 9, h: 7 },
      { x: 13, z: -67, w: 9, h: 7 },
    ],
  },
};

function getLayout() {
  const activeMap = getActiveMapDefinition();
  return MAP_LAYOUTS[activeMap.id] ?? MAP_LAYOUTS.de_outpost_v6;
}

/** Optional bomb state passed to minimap for on-map icon. */
export interface MinimapBombState {
  planted: boolean;
  x?: number;
  z?: number;
}

export interface MinimapPlayerMarker {
  x: number;
  z: number;
  alive: boolean;
  health?: number;
}

const MINIMAP_STYLE_ID = 'minimap-default-styles';
const MINIMAP_DEFAULT_CSS = `
  #minimap-canvas {
    position: fixed;
    top: 10px;
    left: 10px;
    width: ${CANVAS_SIZE}px;
    height: ${CANVAS_SIZE}px;
    pointer-events: none;
    z-index: 30;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 4px;
  }
`;

function ensureMinimapDefaultStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(MINIMAP_STYLE_ID)) return;
  const styleEl = document.createElement('style');
  styleEl.id = MINIMAP_STYLE_ID;
  styleEl.textContent = MINIMAP_DEFAULT_CSS;
  document.head.appendChild(styleEl);
}

export class Minimap {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr: number;
  private showCallouts = true;
  private bombFlashTimer = 0;
  private bombState: MinimapBombState = { planted: false };
  private zoomLevel: 1 | 1.5 = 1;
  private readonly mobile: boolean;
  private zoomButton: HTMLButtonElement | null = null;

  constructor() {
    ensureMinimapDefaultStyles();
    this.mobile = isMobile();

    this.dpr =
      typeof window !== 'undefined' && typeof window.devicePixelRatio === 'number'
        ? Math.max(1, window.devicePixelRatio)
        : 1;

    this.canvas = document.createElement('canvas');
    this.canvas.id = 'minimap-canvas';
    this.canvas.setAttribute('data-hud-element', 'minimap');
    // Backing buffer is sized for DPR; CSS controls the display size so
    // mobile media queries can shrink the minimap.
    this.canvas.width = CANVAS_SIZE * this.dpr;
    this.canvas.height = CANVAS_SIZE * this.dpr;
    document.body.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d')!;
    this.createZoomButton();
  }

  /** Toggle callout label visibility. Returns new state. */
  setCalloutsVisible(visible: boolean): void {
    this.showCallouts = visible;
  }

  /** Get whether callout labels are visible. */
  getCalloutsVisible(): boolean {
    return this.showCallouts;
  }

  /** Update planted bomb position for the minimap icon. */
  setBombState(state: MinimapBombState): void {
    this.bombState = { ...state };
    if (!state.planted) this.bombFlashTimer = 0;
  }

  update(
    localPos: { x: number; z: number },
    localYaw: number,
    remotePlayers: MinimapPlayerMarker[],
    bomb?: MinimapBombState,
    dt?: number,
  ): void {
    const ctx = this.ctx;
    const layout = getLayout();
    const scale = (MAP_PX / (layout.arenaHalf * 2)) * this.zoomLevel;
    const activeBomb = bomb ?? this.bombState;
    this.syncZoomButtonPosition();

    // Reset to identity then scale by DPR so all drawing uses logical
    // (CSS-pixel) coordinates while the backing buffer is hi-DPI crisp.
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    ctx.save();
    ctx.translate(CENTER, CENTER);
    ctx.rotate(-localYaw);

    ctx.fillStyle = 'rgba(120,120,120,0.5)';
    ctx.strokeStyle = 'rgba(180,180,180,0.6)';
    ctx.lineWidth = 1;

    for (const s of layout.structures) {
      const rx = (s.x - localPos.x) * scale;
      const rz = (s.z - localPos.z) * scale;
      const rw = s.w * scale;
      const rh = s.h * scale;
      ctx.fillRect(rx - rw / 2, rz - rh / 2, rw, rh);
      ctx.strokeRect(rx - rw / 2, rz - rh / 2, rw, rh);
    }

    ctx.strokeStyle = 'rgba(255,255,255,0.2)';
    ctx.lineWidth = 1;
    const bx = (-layout.arenaHalf - localPos.x) * scale;
    const bz = (-layout.arenaHalf - localPos.z) * scale;
    const bSize = layout.arenaHalf * 2 * scale;
    ctx.strokeRect(bx, bz, bSize, bSize);

    // ── Callout labels ──
    if (this.showCallouts) {
      const activeMap = getActiveMapDefinition();
      const callouts = getCallouts(activeMap.id);

      ctx.font = CALLOUT_FONT;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      for (const callout of callouts) {
        const cx = (callout.position.x - localPos.x) * scale;
        const cz = (callout.position.z - localPos.z) * scale;

        // Cull labels outside visible area
        if (Math.abs(cx) > CENTER + 10 || Math.abs(cz) > CENTER + 10) continue;

        const textWidth = ctx.measureText(callout.name).width;
        const bgW = textWidth + CALLOUT_PAD_X * 2;
        const bgH = 10 + CALLOUT_PAD_Y * 2;

        // Semi-transparent background pill
        ctx.fillStyle = CALLOUT_BG_COLOR;
        ctx.fillRect(cx - bgW / 2, cz - bgH / 2, bgW, bgH);

        // Counter-rotate text so it's always readable (not rotated with the map)
        ctx.save();
        ctx.translate(cx, cz);
        ctx.rotate(localYaw); // undo map rotation for text
        ctx.fillStyle = CALLOUT_TEXT_COLOR;
        ctx.fillText(callout.name, 0, 0);
        ctx.restore();
      }
    }

    // ── Bomb icon ──
    if (activeBomb.planted && typeof activeBomb.x === 'number' && typeof activeBomb.z === 'number') {
      const bombX = (activeBomb.x - localPos.x) * scale;
      const bombZ = (activeBomb.z - localPos.z) * scale;

      // Update flash timer
      if (dt !== undefined && dt > 0) {
        this.bombFlashTimer += dt;
      }

      // Flash: visible half the time at BOMB_FLASH_RATE Hz
      const flashCycle = this.bombFlashTimer * BOMB_FLASH_RATE;
      const flashOn = (flashCycle % 1) < 0.6; // 60% on, 40% off

      if (flashOn) {
        this.drawBombIcon(ctx, bombX, bombZ);
      }
    } else {
      // Reset flash timer when bomb is not planted
      this.bombFlashTimer = 0;
    }

    // ── Remote players ──
    for (const rp of remotePlayers) {
      if (!rp.alive) continue;
      const dx = (rp.x - localPos.x) * scale;
      const dz = (rp.z - localPos.z) * scale;
      this.drawHealthDot(ctx, dx, dz, rp.health ?? 100);
    }

    ctx.restore();

    // ── Local player icon (always centered, not rotated) ──
    ctx.save();
    ctx.translate(CENTER, CENTER);
    ctx.fillStyle = 'rgba(100,255,100,0.2)';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(-8, -18);
    ctx.lineTo(8, -18);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#44ff44';
    ctx.beginPath();
    ctx.arc(0, 0, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  show(): void {
    this.canvas.style.display = 'block';
    if (this.zoomButton) {
      this.zoomButton.style.display = 'block';
      this.syncZoomButtonPosition();
    }
  }

  hide(): void {
    this.canvas.style.display = 'none';
    if (this.zoomButton) this.zoomButton.style.display = 'none';
  }

  dispose(): void {
    if (this.canvas.parentNode) {
      this.canvas.remove();
    }
    if (this.zoomButton) {
      this.zoomButton.remove();
      this.zoomButton = null;
    }
  }

  private createZoomButton(): void {
    if (!this.mobile) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('aria-label', 'Toggle minimap zoom');
    Object.assign(button.style, {
      position: 'fixed',
      width: '16px',
      height: '16px',
      padding: '0',
      border: '1px solid rgba(255,255,255,0.45)',
      borderRadius: '3px',
      background: 'rgba(0,0,0,0.72)',
      color: '#ffffff',
      font: '700 7px monospace',
      lineHeight: '14px',
      textAlign: 'center',
      cursor: 'pointer',
      zIndex: '31',
      pointerEvents: 'auto',
    });
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.zoomLevel = this.zoomLevel === 1 ? 1.5 : 1;
      this.updateZoomButtonLabel();
    });
    this.zoomButton = button;
    this.updateZoomButtonLabel();
    document.body.appendChild(button);
    this.syncZoomButtonPosition();
  }

  private updateZoomButtonLabel(): void {
    if (!this.zoomButton) return;
    this.zoomButton.textContent = this.zoomLevel === 1 ? '1x' : '1.5';
    this.zoomButton.title = `Minimap zoom: ${this.zoomLevel}x`;
  }

  private syncZoomButtonPosition(): void {
    if (!this.zoomButton) return;
    const rect = this.canvas.getBoundingClientRect();
    this.zoomButton.style.left = `${rect.right - 18}px`;
    this.zoomButton.style.top = `${rect.top + 2}px`;
  }

  private drawHealthDot(ctx: CanvasRenderingContext2D, x: number, z: number, health: number): void {
    const color = this.getHealthDotColor(health);
    const gradient = ctx.createRadialGradient(x - 1, z - 1, 1, x, z, 4);
    gradient.addColorStop(0, 'rgba(255,255,255,0.95)');
    gradient.addColorStop(0.35, color);
    gradient.addColorStop(1, 'rgba(0,0,0,0.35)');

    ctx.fillStyle = 'rgba(0,0,0,0.65)';
    ctx.beginPath();
    ctx.arc(x, z, 4.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, z, 3.4, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawBombIcon(ctx: CanvasRenderingContext2D, x: number, z: number): void {
    ctx.save();
    ctx.translate(x, z);

    ctx.fillStyle = 'rgba(255,40,40,0.28)';
    ctx.beginPath();
    ctx.arc(0, 0, BOMB_ICON_RADIUS + 5, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(255,48,48,0.95)';
    ctx.beginPath();
    ctx.roundRect(-4, -3, 8, 8, 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(255,210,80,0.95)';
    ctx.fillRect(-2, -6, 4, 3);
    ctx.strokeStyle = 'rgba(255,210,80,0.95)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(4, -5, 4, Math.PI, Math.PI * 1.65);
    ctx.stroke();

    ctx.restore();
  }

  private getHealthDotColor(health: number): string {
    const hp = Math.max(0, Math.min(100, health));
    if (hp < 25) return 'rgba(255,60,60,0.95)';
    if (hp <= 50) return 'rgba(255,220,70,0.95)';
    return 'rgba(80,255,100,0.95)';
  }
}
